import React, {useEffect, useMemo, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {getIsProactiveOfferCheckResolved} from 'modules/accommodation/selectors'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {
  getAccommodationPageHotel,
  getStructuredDataMarkupOffer
} from 'modules/searchApi/selectors'
import Head from 'next/head'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {buildHotelRoomPriceSchema} from '@vio/shared/src/structuredData'

export const RoomPriceStructuredData: React.FC = () => {
  const dispatch = useDispatch()
  const toggle = useToggle()

  const isEnabled = toggle('feature-hotel-price-structured-data', false, true)
  const isOfferCheckOnPpEnabled = toggle(
    'feature-offer-check-on-pp',
    false,
    true
  )
  const isPreClickOfferCheckEnabled =
    toggle('feature-offer-check-selected-offer-ap', false, true) &&
    !isOfferCheckOnPpEnabled

  const hotel = useSelector(getAccommodationPageHotel)
  const searchParameters = useSelector(getSearchParameters)
  const currencyCode = useSelector(getCurrencyCode)
  const isProactiveOfferCheckResolved = useSelector(
    getIsProactiveOfferCheckResolved
  )

  // The emitted offer is the one tagged as the user's selection (preferred_rate).
  // The proactive offer check fires on this same offer (see proactiveOfferCheck
  // middleware) so the validated price always matches the price emitted here.
  const emittedOffer = useSelector(getStructuredDataMarkupOffer)

  const isSoldOut = !!useGetIsOfferSoldOut({offer: emittedOffer?.offer})

  const schema = useMemo(() => {
    if (
      !isEnabled ||
      !hotel ||
      !currencyCode ||
      !searchParameters.checkIn ||
      !searchParameters.checkOut ||
      !emittedOffer
    ) {
      return null
    }

    // On the B side of feature-offer-check-selected-offer-ap hold the first
    // render until the proactive offer-check has settled
    if (isPreClickOfferCheckEnabled && !isProactiveOfferCheckResolved) {
      return null
    }

    const {room, offerPrice} = emittedOffer

    return buildHotelRoomPriceSchema({
      hotel,
      room: {
        id: room.id,
        name: room.name,
        occupationPerRoom: room.occupationPerRoom
      },
      offerPrice,
      checkIn: searchParameters.checkIn,
      checkOut: searchParameters.checkOut,
      currencyCode,
      isSoldOut
    })
  }, [
    isEnabled,
    hotel,
    emittedOffer,
    searchParameters,
    currencyCode,
    isSoldOut,
    isPreClickOfferCheckEnabled,
    isProactiveOfferCheckResolved
  ])

  const servedRef = useRef(false)
  useEffect(() => {
    if (!schema || servedRef.current) return
    servedRef.current = true
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.HotelStructuredDataMarkup,
        action: Action.Served,
        payload: {
          page: 'Accommodation',
          schema,
          isSoldOut
        }
      })
    )
  }, [schema, isSoldOut, dispatch])

  if (!schema) return null

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schema)
        }}
      />
    </Head>
  )
}
